import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Window, WindowContent, Frame, Button } from 'react95';
import { userSelector } from '../../selectors/authSelectors';
import { setModalType } from '../../actions/taskDetailActions';
import {
  useUpdateOpsReviewMutation,
  useUpdateConfirmAmountsMutation,
  useUpdateFinalReviewMutation,
  useUpdateFiledWithIrsMutation,
  useUpdateAcceptedByIrsMutation,
  useUpdateNotStartedMutation,
  useUpdateTaxExpertEscMutation,
  useUpdateEngEscMutation,
  useUpdateDoNotFileMutation,
  useCreateTaskSubstagesMutation,
  useGetTaskQuery
} from '../../api/opsSystemApi';
import { getActionConfirmationMsg } from '../../utils/taskUtils';
import './ConfirmationModal.scss';
import { TASK_DETAIL_ACTIONS, TASK_SUBSTAGES } from '../../global/Environment';
import { useUploadDepositWithdrawalDataMutation } from '../../api/taxesApi';
import { useUploadToDrakeMutation, useUpdateDrakeAmountsMutation } from '../../api/drakeApi';
import { get } from 'lodash-es';
import { useParams } from 'react-router-dom';

const ConfirmationModal = ({ modalType, onConfirm, onCancel }) => {
  const dispatch = useDispatch();

  const user = useSelector(userSelector);

  const { taskId } = useParams();
  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId);

  const [updateOpsReview] = useUpdateOpsReviewMutation();
  const [updateConfirmAmounts] = useUpdateConfirmAmountsMutation();
  const [updateFinalReview] = useUpdateFinalReviewMutation();
  const [updateFiledWithIrs] = useUpdateFiledWithIrsMutation();
  const [updateAcceptedByIrs] = useUpdateAcceptedByIrsMutation();
  const [updateNotStarted] = useUpdateNotStartedMutation();
  const [updateTaxExpertEsc] = useUpdateTaxExpertEscMutation();
  const [updateEngEsc] = useUpdateEngEscMutation();
  const [updateDoNotFile] = useUpdateDoNotFileMutation();
  const [createTaskSubstages] = useCreateTaskSubstagesMutation();
  const [uploadToDrake] = useUploadToDrakeMutation();
  const [updateDrakeAmounts] = useUpdateDrakeAmountsMutation();
  const [uploadDepositWithdrawalData] = useUploadDepositWithdrawalDataMutation();

  const { email: reviewer } = user;

  const ACTION_HANDLERS = {
    [TASK_DETAIL_ACTIONS.UPLOAD_TAX_DATA]: () => uploadToDrake({ taskId }),
    [TASK_DETAIL_ACTIONS.UPLOAD_DEPOSIT_WITHDRAWAL_FORMS]: () => uploadDepositWithdrawalData({ taskId }),
    [TASK_DETAIL_ACTIONS.UNLOCK_USER]: () => updateNotStarted({ taskId }),
    [TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS]: () => updateConfirmAmounts({ taskId, reviewer, sendNotification: true }),
    [TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS_NO_TEXT_EMAIL]: () =>
      updateConfirmAmounts({ taskId, sendNotification: false }),
    [TASK_DETAIL_ACTIONS.UPDATE_AMOUNTS]: () => updateDrakeAmounts({ taskId }),
    [TASK_DETAIL_ACTIONS.FILED_WITH_IRS]: () => updateFiledWithIrs({ taskId }),
    [TASK_DETAIL_ACTIONS.ACCEPTED_BY_IRS]: () => updateAcceptedByIrs({ taskId, reviewer }),
    [TASK_DETAIL_ACTIONS.MOVE_TO_TAX_EXPERT_ESC]: () => updateTaxExpertEsc({ taskId }),
    [TASK_DETAIL_ACTIONS.MOVE_TO_OPS_REVIEW]: () => updateOpsReview({ taskId, reviewer }),
    [TASK_DETAIL_ACTIONS.MOVE_TO_FINAL_REVIEW]: () => updateFinalReview({ taskId, reviewer }),
    [TASK_DETAIL_ACTIONS.MOVE_TO_ENG_ESC]: () => updateEngEsc({ taskId, reviewer }),
    [TASK_DETAIL_ACTIONS.DO_NOT_FILE]: () => updateDoNotFile({ taskId }),
    [TASK_SUBSTAGES.BLOCKED]: () => createTaskSubstages({ taskId, names: [TASK_SUBSTAGES.BLOCKED] }),
    [TASK_SUBSTAGES.EMAIL_OUTBOX]: () => createTaskSubstages({ taskId, names: [TASK_SUBSTAGES.EMAIL_OUTBOX] }),
    [TASK_SUBSTAGES.PAST_YEAR_RETURN]: () => createTaskSubstages({ taskId, names: [TASK_SUBSTAGES.PAST_YEAR_RETURN] })
  };

  const runTaskDetailAction = async (action) => {
    const handler = get(ACTION_HANDLERS, action);
    if (handler) return await handler();
  };

  if (!modalType || !task?.id) {
    return null;
  }

  const handleConfirm = () => {
    if (onConfirm != null) {
      onConfirm();
    } else {
      runTaskDetailAction(modalType);
    }
  };

  const handleCancel = () => {
    if (onCancel != null) {
      onCancel();
    } else {
      dispatch(setModalType(null));
    }
  };

  return (
    <Window className='confirmation-modal'>
      <WindowContent>
        <Frame variant='field' className='confirmation-modal-body'>
          <div className='confirmation-modal-text'>{getActionConfirmationMsg(modalType)}?</div>
          <div className='confirmation-modal-btns'>
            <Button className='confirmation-modal-cancel' variant='flat' onClick={handleCancel}>
              Cancel
            </Button>
            <Button primary variant='flat' onClick={handleConfirm} disabled={isTaskLoading}>
              OK
            </Button>
          </div>
        </Frame>
      </WindowContent>
    </Window>
  );
};

export default ConfirmationModal;
